<script lang="ts" module>
	import { MAX_MINOR } from '../domain/pricing';
	import { parseTypedMinor } from '../format/money';

	// Twelve digits is more cash than any till holds.
	const TYPED_LENGTH = 12;

	/**
	 * The tender once one more key is typed, or null when the keypad does not
	 * take the key: the order store takes no tender above MAX_MINOR (`Minor`).
	 */
	export function typeTender(typed: string, key: string): string | null {
		const next = typed + key;
		if (next.length > TYPED_LENGTH) return null;
		return (parseTypedMinor(next) ?? 0) <= MAX_MINOR ? next : null;
	}
</script>

<script lang="ts">
	// UC-5 and UC-16: the body of the payment sheet. Two taps for the common
	// sale, three for the rest.
	//
	// Exact cash and Card commit on one tap, because "two burgers, exact cash"
	// is most of a shift. The quick amounts and the keypad only fill the
	// tender: they show the change live and leave Confirm, in the sheet's
	// header, to commit. The till holds the tender, so Confirm can read it.
	//
	// An order whose total is 0, a new sale or one collected from Orders, takes
	// no money, so there is no tender to type: the cash amounts and the keypad
	// are off, and the tender and change show none. Exact sends a tender of 0,
	// and Card stays, as the plan pays a free order (UC-35); the server refuses
	// any other cash tender. When a discount made it free, the sheet says the
	// drawer stays shut.
	//
	// A card payment of a sent delivery that names a driver needs a manager
	// (UC-16.R9): for anyone else signed in, the sheet says so under Card, and
	// Card opens Manager approval (T20), which the till puts in its place.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import Keypad from '../components/Keypad.svelte';
	import Notice from '../components/Notice.svelte';
	import type { PayNotice } from '../till/shift.svelte';
	import { message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import { change } from '../domain/change';
	import { suggestTenders } from '../domain/tender';

	/** Which one-tap payment is in flight, so that button says so in words. */
	export type PayWay = 'exact' | 'card' | 'confirm' | null;

	interface Props {
		language: Language;
		totalMinor: number;
		minorDigits: number;
		quickTenderSteps: number[];
		/** The tender typed so far, in minor units as digits. */
		typed: string;
		onType: (typed: string) => void;
		working: PayWay;
		failure: string;
		onCash: (tenderedMinor: number) => void;
		onCard: () => void;
		/** The order being paid has a discount. */
		discounted: boolean;
		/**
		 * Why the till takes no payment now (UC-60), or null while it takes
		 * one; its action opens Open shift (T30) over this sheet.
		 */
		shiftNotice: PayNotice | null;
		onOpenShift: () => void;
		/** Card asks a manager first: a driver's delivery, with no manager signed in (UC-16.R9). */
		cardAsksManager: boolean;
	}

	let {
		language,
		totalMinor,
		minorDigits,
		quickTenderSteps,
		typed,
		onType,
		working,
		failure,
		onCash,
		onCard,
		discounted,
		shiftNotice,
		onOpenShift,
		cardAsksManager,
	}: Props = $props();

	const busy = $derived(working !== null);
	// While the till takes no payment (UC-60) the Notice says why, and Exact,
	// Card and Confirm are off; typing a tender takes nothing.
	const noPayment = $derived(shiftNotice !== null);
	const tenderedMinor = $derived(parseTypedMinor(typed));
	const quick = $derived(suggestTenders(totalMinor, quickTenderSteps));
	const result = $derived(change('cash', totalMinor, tenderedMinor ?? 0));
	const nothingToTake = $derived(totalMinor === 0);
	const free = $derived(nothingToTake && discounted);
</script>

<div class="payment" data-testid="panel-payment">
	{#if shiftNotice}
		<Notice
			life="standing"
			tone="flag"
			testId="pay-shift-notice"
			action={shiftNotice.action ?? undefined}
			actionTestId="pay-shift-open"
			onAction={shiftNotice.action ? onOpenShift : undefined}
		>
			{shiftNotice.text}
		</Notice>
	{/if}
	{#if failure}
		<Band tone="flag" testId="pay-error">{failure}</Band>
	{/if}
	<div class="body">
		<div class="settle">
			<div class="total">
				<span class="label">{message(language, 'sellTotal')}</span>
				<output class="figure" dir="ltr" data-testid="pay-total">
					{formatMinor(totalMinor, minorDigits)}
				</output>
				{#if free}
					<span class="free" data-testid="pay-free">{message(language, 'payFree')}</span>
				{/if}
			</div>
			<div class="commit">
				<Button
					testId="pay-cash-exact"
					icon="cash"
					label={message(language, 'payExact')}
					state={working === 'exact' ? 'pending' : 'idle'}
					pendingLabel={message(language, 'payFinishing')}
					disabled={busy || noPayment}
					onPress={() => onCash(totalMinor)}
				/>
				<Button
					testId="pay-card"
					icon="card"
					label={message(language, 'payCard')}
					tone="ink"
					state={working === 'card' ? 'pending' : 'idle'}
					pendingLabel={message(language, 'payFinishing')}
					disabled={busy || noPayment}
					onPress={onCard}
				/>
			</div>
			{#if cardAsksManager}
				<p class="asks" data-testid="pay-card-asks-manager">
					{message(language, 'payCardAsksManager')}
				</p>
			{/if}
			<div class="quick">
				<span class="label">{message(language, 'payCashAmount')}</span>
				<div class="amounts" dir="ltr">
					{#each quick as amount, index (amount)}
						<Button
							testId="pay-quick-{index}"
							label={formatMinor(amount, minorDigits)}
							tone="secondary"
							size="md"
							mono
							disabled={busy || nothingToTake}
							onPress={() => onType(String(amount))}
						/>
					{/each}
				</div>
			</div>
			<div class="live">
				<Field
					testId="pay-tendered"
					label={message(language, 'payTendered')}
					value={tenderedMinor === null || nothingToTake
						? '–'
						: formatMinor(tenderedMinor, minorDigits)}
					mono
				/>
				<Field
					testId="pay-change"
					label={message(language, 'payChange')}
					value={tenderedMinor === null || nothingToTake
						? '–'
						: formatMinor(result.changeMinor, minorDigits)}
					mono
				/>
			</div>
		</div>
		<div class="pad">
			<Keypad
				testId="pay-pad"
				label={message(language, 'payAmount')}
				deleteLabel={message(language, 'backspace')}
				disabled={busy || nothingToTake}
				canType={typeTender(typed, '0') !== null}
				canDelete={typed.length > 0}
				onKey={(key) => {
					const next = typeTender(typed, key);
					if (next !== null) onType(next);
				}}
				onBackspace={() => onType(typed.slice(0, -1))}
			/>
		</div>
	</div>
</div>

<style>
	.payment {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.body {
		flex: 1;
		min-block-size: 0;
		display: flex;
		gap: var(--space-9);
	}

	.settle {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.total {
		display: flex;
		flex-direction: column;
	}

	.label {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.figure {
		font-family: var(--font-mono);
		font-size: var(--figure-md);
		font-weight: 700;
		line-height: var(--leading-tight);
		letter-spacing: var(--tracking-tight);
		text-align: start;
	}

	.free {
		font-size: var(--text-sm);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--text-strong);
	}

	.commit {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
		gap: var(--touch-gap);
	}

	.quick {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.amounts {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: var(--touch-gap);
	}

	.live {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: var(--space-5);
	}

	.pad {
		flex: none;
		align-self: flex-start;
	}

	.asks {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}
</style>
